import React, { FC } from "react";
import { Link } from "react-router-dom";
import { IntegrationStatusQuery, IntegrationType } from "generated/graphql";
import { existsAndNotEmpty } from "stentor-utils";
import { Button, PageAlert } from "@xapp/components";

import { JobberIntegrationCard } from "./JobberIntegrationCard";
import { LACRMIntegrationCard } from "./LACRMIntegrationCard";
import { ServiceTitanIntegrationCard } from "./ServiceTitanIntegrationCard";
import { IntegrationsListDialog } from "./IntegrationsListDialog";

import style from "./style.scss";

export interface IntegrationsListProps {
    /**
     * The app created in the onboarding that we are integrating to
     */
    app: { appId: string, organizationId: string, leadsContact?: string };
    /**
     * Disable all interactions
     */
    disabled?: boolean;
    /**
     * Only show one of the integrations
     */
    limitIntegrations?: string[];
    /**
     * Does the current tier support integrations
     */
    supportsIntegrations?: boolean;
    /**
     * Respose query integrationStatus
     *
     * Note: This is a bit too specific to the API and will change if the API changes, we should abstract it
     */
    data?: IntegrationStatusQuery;
    /**
     * Add Integration dialog is active
     */
    active?: boolean;
    /**
     * Special mode where we show all the integration cards to allow you to pick them and we do not display the upgrade plan message.
     */
    onboardingMode?: boolean;
    /**
     * User has requested to add an integration.
     */
    onAddIntegration?(): void;
    /**
     * Dialog cancel
     */
    onCancel?(): void;
    /**
     * User requested to disconnect the integration.
     *
     * @param type
     */
    onDisconnect?(type: IntegrationType): void;
}

export const IntegrationsList: FC<IntegrationsListProps> = ({
    active,
    app,
    data,
    disabled,
    limitIntegrations,
    onAddIntegration,
    onCancel,
    supportsIntegrations,
    onboardingMode,
    onDisconnect
}: IntegrationsListProps) => {

    // What does linked and active mean?
    // isLinked - User has set up a link to jobber
    // isActive - The link works

    const isLACRMLinked: boolean = data?.app?.lacrm?.isLinked;
    const isLACRMActive: boolean = data?.app?.lacrm?.isActive;

    const isJobberLinked: boolean = data?.app?.jobber?.isLinked;
    const isJobberActive: boolean = data?.app?.jobber?.isActive;

    const isServiceTitanLinked: boolean = data?.app?.servicetitan?.isLinked;
    const isServiceTitanActive: boolean = data?.app?.servicetitan?.isActive;

    const availableIntegrations: string[] = existsAndNotEmpty(limitIntegrations) ? limitIntegrations : [
        "lacrm",
        "jobber",
        "surefire",
        "servicetitan",
    ];

    const linkedIntegrations: string[] = [];

    if (isLACRMLinked) {
        linkedIntegrations.push('lacrm');
    }

    if (isJobberLinked) {
        linkedIntegrations.push("jobber");
    }

    if (isServiceTitanLinked) {
        linkedIntegrations.push("servicetitan");
    }

    const hasLinkedIntegration: boolean = existsAndNotEmpty(linkedIntegrations);

    return (
        <>
            {!supportsIntegrations && !onboardingMode ? (
                <div>
                    <PageAlert background="orange">
                        <p>Integrations are not supported on your current plan.  Please upgrade to one of our paid tiers to integrate with 3rd party service providers.</p>

                        <div style={{ display: "flex", flexDirection: "column", rowGap: "10px", alignItems: "center" }}>
                            <Link to={`/${app.organizationId}/${app.appId}/upgrade`}>
                                <Button variation="primary" label="Upgrade Plan" />
                            </Link>
                        </div>
                    </PageAlert>
                </div>
            ) : <></>}

            <div className={style.listContainer}>
                {(availableIntegrations.includes("lacrm") && onboardingMode || isLACRMLinked) && (
                    <LACRMIntegrationCard
                        disabled={disabled}
                        appId={app?.appId}
                        isActive={isLACRMActive}
                        isLinked={isLACRMLinked}
                        onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "lacrm") : undefined}
                    />
                )}
                {(availableIntegrations.includes("jobber") && onboardingMode || isJobberLinked) && (
                    <JobberIntegrationCard
                        disabled={disabled}
                        appId={app?.appId}
                        isActive={isJobberActive}
                        isLinked={isJobberLinked}
                        onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "jobber") : undefined}
                    />
                )}
                {(availableIntegrations.includes("servicetitan") && onboardingMode || isServiceTitanLinked) && (
                    <ServiceTitanIntegrationCard
                        disabled={disabled}
                        appId={app?.appId}
                        isActive={isServiceTitanActive}
                        isLinked={isServiceTitanLinked}
                        onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "servicetitan") : undefined}
                    />
                )}
            </div>

            {!hasLinkedIntegration && typeof onAddIntegration === "function" ? (
                <div className={style.noIntegrationContainer}>
                    <h3>You don't have any integrations setup yet.</h3>
                    <Button label="Add Integration" onClick={onAddIntegration} disabled={!supportsIntegrations} />
                </div>
            ) : <></>}

            <IntegrationsListDialog
                appId={app?.appId}
                active={active}
                integrations={availableIntegrations}
                onCancel={onCancel}
            />
        </>
    );
}